// @givmo/passkeys — recovery codes, shown once (AS-WEB14; D-AS-21's A3).
//
// The plaintext codes cross the wire exactly once — in the mint (or regenerate) response — and are
// stored hashed, so this view is the only time anyone can read them (backend AS-BE7a). Nothing here
// keeps them: no storage, no log, no state outliving the dialog. "Copy all" and "Download" hand them
// to the person; "I've saved these" is the only way on, as on iOS.
//
// The CONTENT of a dialog: it draws its own title, and its host places it.

import { useState } from 'react';
import { Button, Dialog, ErrorMessage, Icon, testIdProp } from '@givmo/design-system';
import { Copy, Download } from 'lucide-react';
import { AUTHENTICATOR_WORDS as WORDS } from './authenticator-copy';

const PREFIX = 'recovery-codes';
const TITLE = 'font-sans text-s18 font-w700 tracking-givmo text-text-strong';
const LINE = 'font-sans text-s14 font-w400 tracking-givmo text-text-secondary';

/** The downloaded file's name. */
export const RECOVERY_CODES_FILE_NAME = 'givmo-recovery-codes.txt';

/** Hand the codes to the person as a text file: one per line, nothing else. */
function downloadCodes(codes: readonly string[]): void {
  const url = URL.createObjectURL(new Blob([`${codes.join('\n')}\n`], { type: 'text/plain' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = RECOVERY_CODES_FILE_NAME;
  anchor.click();
  URL.revokeObjectURL(url);
}

export interface RecoveryCodesViewProps {
  codes: readonly string[];
  /** A line to show above the codes instead of nothing (a mint that could not finish, say). */
  notice?: string;
  /** "I've saved these". */
  onSaved: () => void;
}

export function RecoveryCodesView({ codes, notice, onSaved }: RecoveryCodesViewProps) {
  const [copyFailed, setCopyFailed] = useState(false);
  return (
    <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-view`)}>
      <Dialog.Title className={TITLE}>{WORDS.codesTitle}</Dialog.Title>
      <Dialog.Description className={LINE}>{WORDS.codesLine}</Dialog.Description>
      {notice !== undefined && <p className={LINE}>{notice}</p>}
      <ul
        className="grid grid-cols-2 gap-x-24 gap-y-10 rounded-lg border border-border-subtle bg-surface-sunken px-20 py-16 font-sans text-s14 font-w600 tracking-wider text-text-strong max-sm:grid-cols-1"
        {...testIdProp(`${PREFIX}-list`)}
      >
        {codes.map((code) => (
          <li key={code} className="whitespace-nowrap">
            {code}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-8">
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          leadingIcon={<Icon icon={Copy} size={14} aria-hidden />}
          onClick={() => {
            setCopyFailed(false);
            void navigator.clipboard?.writeText(codes.join('\n')).catch(() => setCopyFailed(true));
          }}
          testId={`${PREFIX}-copy`}
        >
          {WORDS.copyAll}
        </Button>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          leadingIcon={<Icon icon={Download} size={14} aria-hidden />}
          onClick={() => downloadCodes(codes)}
          testId={`${PREFIX}-download`}
        >
          {WORDS.download}
        </Button>
      </div>
      {copyFailed && (
        <ErrorMessage className="pt-0" testId={`${PREFIX}-copy-failed`}>
          {WORDS.failed}
        </ErrorMessage>
      )}
      <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {WORDS.codesNote}
      </p>
      <Button
        appearance="saas"
        tone="primary"
        size="lg"
        fullWidth
        onClick={onSaved}
        testId={`${PREFIX}-saved`}
      >
        {WORDS.saved}
      </Button>
    </div>
  );
}
